/**
 * useLanguage hook — read and change the UI language.
 *
 * Centralizes the logic of:
 *   - reading current language from Redux
 *   - dispatching change to Redux (which persists to localStorage)
 *   - syncing i18next to match
 *
 * Components should never touch i18n.changeLanguage() directly —
 * always use this hook.
 */

import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { selectLanguage, setLanguage } from '@/store/uiSlice';

export function useLanguage() {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const language = useSelector(selectLanguage);

  /** @param {'fa'|'en'} next */
  const changeLanguage = useCallback(
    (next) => {
      if (next === language) return;
      dispatch(setLanguage(next));
      i18n.changeLanguage(next);
    },
    [dispatch, i18n, language]
  );

  const toggle = useCallback(() => {
    changeLanguage(language === 'fa' ? 'en' : 'fa');
  }, [language, changeLanguage]);

  return {
    language,
    isRtl: language === 'fa',
    changeLanguage,
    toggle,
  };
}
